import { NgClass } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { RouterLink, RouterLinkActive } from "@angular/router";

import { AbstractThemingService } from "@bitwarden/angular/platform/services/theming/theming.service.abstraction";
import { ThemeTypes } from "@bitwarden/common/platform/enums";
import { SideNavService } from "@bitwarden/components";
import { I18nPipe } from "@bitwarden/ui-common";

@Component({
  selector: "brackets-vault-nav-logo",
  imports: [NgClass, RouterLink, RouterLinkActive, I18nPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: "tw-contents" },
  template: `
    <div
      class="tw-px-2"
      [class.tw-bg-bg-nav]="variant() === 'default'"
      [class.bv-admin-nav-logo]="variant() === 'admin'"
      [ngClass]="{
        'tw-sticky tw-top-0 tw-z-50 tw-pb-4': sideNavService.open(),
        'tw-pb-2': !sideNavService.open(),
        'tw-pt-[calc(0.5rem_+_var(--bit-sidenav-extra-top-padding,_0px))]':
          sideNavService.isOverlay(),
        'tw-pt-2': !sideNavService.isOverlay(),
      }"
    >
      <div
        class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-rounded-md tw-p-2"
        [class.tw-flex-col]="!sideNavService.open()"
        [class.tw-justify-center]="!sideNavService.open()"
      >
        <a
          routerLink="."
          class="tw-relative tw-min-w-0 tw-rounded-md tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-inset focus-visible:tw-ring-border-nav-focus"
          [class.tw-flex-1]="sideNavService.open()"
          [class.tw-overflow-hidden]="sideNavService.open()"
          [class.tw-h-[55px]]="sideNavService.open()"
          [class.tw-h-9]="!sideNavService.open()"
          [class.tw-w-9]="!sideNavService.open()"
          [class.tw-flex-none]="!sideNavService.open()"
          aria-label="BracketsVault"
          title="BracketsVault"
          routerLinkActive
          ariaCurrentWhenActive="page"
        >
          <img
            [src]="logoSrc()"
            alt="BracketsVault"
            class="tw-size-full tw-object-contain"
            [class.tw-max-w-full]="sideNavService.open()"
            [class.tw-object-left]="sideNavService.open()"
            [class.tw-object-center]="!sideNavService.open()"
          />
        </a>
        <button
          type="button"
          class="tw-flex tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-border-0 tw-bg-transparent tw-p-1.5 tw-outline-none hover:tw-bg-bg-nav-hover focus-visible:tw-ring focus-visible:tw-ring-inset focus-visible:tw-ring-border-nav-focus"
          [class.tw-ml-auto]="sideNavService.open()"
          [class.tw-ml-0]="!sideNavService.open()"
          (click)="sideNavService.toggle()"
          [attr.aria-expanded]="sideNavService.open()"
          [attr.aria-label]="'toggleSideNavigation' | i18n"
          aria-controls="bit-side-nav"
        >
          <img
            src="images/icons/brackets-vault/Sidepanel.svg"
            alt=""
            class="tw-h-5 tw-w-[19px] tw-object-contain"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  `,
})
export class BracketsVaultNavLogoComponent {
  protected readonly sideNavService = inject(SideNavService);
  private readonly themingService = inject(AbstractThemingService);

  /** Resolved theme (System follows OS light/dark), matching `theme_dark` on `<html>`. */
  private readonly effectiveTheme = toSignal(this.themingService.theme$, {
    initialValue: ThemeTypes.Light,
  });

  /** `admin` uses the white logo on the dark admin sidebar. */
  readonly variant = input<"default" | "admin">("default");

  protected readonly logoSrc = computed(() => {
    if (!this.sideNavService.open()) {
      return "images/icons/brackets-vault/Logomark.svg";
    }

    if (this.variant() === "admin" || this.effectiveTheme() === ThemeTypes.Dark) {
      return "images/icons/brackets-vault/brackets-vault-white-logo.svg";
    }

    return "images/logo.png";
  });
}
